import React, { useState } from 'react';
import { Mail, Sparkles, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

/**
 * Small inline email capture for blog post pages.
 * Submits to the same Formspree endpoint used by the contact form,
 * tagged with `source` so Ayumi can filter newsletter signups in Formspree.
 */
const BlogEmailCapture = ({ source = 'blog' }) => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);

    try {
      const response = await fetch('https://formspree.io/f/maqkqowk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source,
          _subject: `New blog subscriber from ${source}`,
          message: `Newsletter signup from blog post: ${source}`,
        }),
      });

      if (!response.ok) throw new Error('Submission failed');

      setDone(true);
      setEmail('');
      toast({
        title: 'You\'re in!',
        description:
          'Thanks for subscribing. You\'ll receive occasional appointment reminders and wellness tips from Ayumi.',
      });
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <aside
      className="mt-12 rounded-2xl border border-teal-100 bg-gradient-to-br from-white via-teal-50/40 to-rose-50/40 p-8 shadow-sm"
      data-testid="blog-email-capture"
      aria-label="Subscribe to wellness tips and appointment reminders"
    >
      <div className="flex flex-col md:flex-row md:items-center md:gap-8">
        <div className="flex-1 mb-6 md:mb-0">
          <div className="flex items-center gap-2 mb-2 text-teal-700">
            <Sparkles className="w-5 h-5" />
            <span className="text-xs font-semibold uppercase tracking-wider">
              Stay connected
            </span>
          </div>
          <h3 className="text-2xl font-serif text-gray-800 mb-2">
            Get appointment reminders + wellness tips
          </h3>
          <p className="text-gray-600 text-sm leading-relaxed">
            One short, thoughtful email a month. Booking reminders, seasonal
            self-care tips, and the occasional special offer from Ayumi&apos;s
            mid-town Sacramento practice. No spam, unsubscribe anytime.
          </p>
        </div>

        <div className="flex-1 w-full md:max-w-sm">
          {done ? (
            <div
              className="flex items-center gap-3 rounded-xl bg-teal-600 px-5 py-4 text-white"
              data-testid="blog-email-success"
            >
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">
                You&apos;re subscribed — see you soon.
              </span>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-3"
              data-testid="blog-email-form"
            >
              <label htmlFor={`blog-email-${source}`} className="sr-only">
                Email address
              </label>
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  id={`blog-email-${source}`}
                  type="email"
                  required
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder:text-gray-400 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100 transition-all duration-200"
                  data-testid="blog-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200 hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="blog-email-submit"
              >
                {submitting ? 'Subscribing…' : 'Subscribe'}
              </button>
            </form>
          )}
        </div>
      </div>
    </aside>
  );
};

export default BlogEmailCapture;
